import { ChangeEvent, createContext, FC, useContext, useEffect, useState } from 'react';
import { Area } from 'react-easy-crop';

import {
  useSaveProfilePhotoMutation,
  useDeleteProfilePhotoMutation,
} from 'services/talentoAPI/hiringAPI';
import { selectUserInfo, setProfilePhoto } from 'store';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { convertBytesToMB, getCroppedImg } from 'utils/helpers';

import { MB_LIMIT_FILES } from '../utils/constants';

interface ICropImageContext {
  openModal: boolean;
  setOpenModal: (value: boolean) => void;
  closeModal: () => void;
  imageUrl: string | null;
  setImageUrl: (value: string | null) => void;
  setSavedCroppedAreaPixels: (value: Area | null) => void;
  isLoadingImage: boolean;
  setIsLoadingImage: (value: boolean) => void;
  saveImg: () => Promise<void>;
  isErrorSaving: boolean;
  isWarningExit: boolean;
  isLoadingSaveImage: boolean;
  successfulUpload: boolean | null;
  setSuccessfulUpload: (value: boolean | null) => void;
  secureExitModal: () => void;
  returnEditingView: () => void;
  cleanImg: () => void;
  onLoadImage: (e: ChangeEvent<HTMLInputElement>) => void;
}

const cropImageContext = createContext<ICropImageContext>({
  openModal: true,
  setOpenModal: (value: boolean) => {},
  closeModal: () => {},
  imageUrl: null,
  setImageUrl: (value: string | null) => {},
  setSavedCroppedAreaPixels: (value: Area | null) => {},
  isLoadingImage: false,
  setIsLoadingImage: (value: boolean) => {},
  saveImg: () => new Promise((resolve) => resolve()),
  isErrorSaving: false,
  isWarningExit: false,
  isLoadingSaveImage: false,
  successfulUpload: null,
  setSuccessfulUpload: (value: boolean | null) => {},
  secureExitModal: () => {},
  returnEditingView: () => {},
  cleanImg: () => {},
  onLoadImage: (e) => {},
});

export const CropImageContextProvider: FC = ({ children }) => {
  const dispatch = useAppDispatch();
  const { email, profilePhoto } = useAppSelector(selectUserInfo);

  const [openModal, setOpenModal] = useState<boolean>(false);
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [savedCroppedAreaPixels, setSavedCroppedAreaPixels] = useState<Area | null>(null);
  const [isLoadingImage, setIsLoadingImage] = useState<boolean>(false);
  const [isErrorSaving, setIsErrorSaving] = useState<boolean>(false);
  const [isWarningExit, setIsWarningExit] = useState<boolean>(false);
  const [isLoadingSaveImage, setIsLoadingSaveImage] = useState<boolean>(false);
  const [successfulUpload, setSuccessfulUpload] = useState<boolean | null>(null);

  const [removeExistingPhoto] = useDeleteProfilePhotoMutation();
  const [postProfilePhoto] = useSaveProfilePhotoMutation();

  const closeModal = (autoSave: boolean = false) => {
    setIsErrorSaving(false);
    setIsWarningExit(false);
    setOpenModal(false);
    if (autoSave) setImageUrl(profilePhoto);
  };

  const cleanImg = async () => {
    if (imageUrl) URL.revokeObjectURL(imageUrl);
    setImageUrl(null);
    setSavedCroppedAreaPixels(null);
  };

  const loadImgTemp = (file: File) => {
    if (imageUrl) URL.revokeObjectURL(imageUrl);

    const newImageUrl = URL.createObjectURL(file);

    setImageUrl(newImageUrl);
  };

  const setErrorImageProcessing = () => {
    setSuccessfulUpload(false);
    setIsErrorSaving(true);
    setIsWarningExit(false);
  };

  const setWarningImageProcessing = () => {
    setSuccessfulUpload(false);
    setIsErrorSaving(false);
    setIsWarningExit(true);
  };

  const setSuccessfulyImageProcessing = () => {
    setSuccessfulUpload(true);
    setIsErrorSaving(false);
    setIsWarningExit(false);
  };

  const secureExitModal = () => {
    if (imageUrl && !isErrorSaving && !isWarningExit) {
      setWarningImageProcessing();
    } else {
      closeModal(true);
    }
  };

  const returnEditingView = () => {
    setIsWarningExit(false);
  };

  const onLoadImage = (e: ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files?.[0]) {
      return;
    }
    const file = e.target.files[0];

    setIsLoadingImage(true);

    if (convertBytesToMB(file.size) <= MB_LIMIT_FILES) {
      loadImgTemp(file);
    } else {
      console.error(
        'Image is bigger than allowed ' + MB_LIMIT_FILES + 'MB: ' + convertBytesToMB(file.size)
      );
    }

    setInterval(() => {
      setIsLoadingImage(false);
    }, 2000);
  };

  const saveImg = async () => {
    if (!imageUrl || !savedCroppedAreaPixels) return;
    setIsLoadingSaveImage(true);

    try {
      if (profilePhoto) await removeExistingPhoto().unwrap();

      const croppedImageBase64 = (await getCroppedImg(imageUrl, savedCroppedAreaPixels, {
        outputType: 'base64',
        format: 'image/jpeg',
      })) as string;

      await postProfilePhoto({ email, photo: croppedImageBase64 }).unwrap();
      dispatch(setProfilePhoto({ profilePhoto: croppedImageBase64 }));

      setSuccessfulyImageProcessing();
      closeModal();
    } catch (error) {
      console.error('Error al procesar la imagen:', error);
      setErrorImageProcessing();
    } finally {
      setIsLoadingSaveImage(false);
    }
  };

  useEffect(() => {
    setImageUrl(profilePhoto);
  }, [profilePhoto]);

  const value = {
    openModal,
    setOpenModal,
    closeModal,
    imageUrl,
    setImageUrl,
    setSavedCroppedAreaPixels,
    isLoadingImage,
    setIsLoadingImage,
    saveImg,
    isErrorSaving,
    isWarningExit,
    isLoadingSaveImage,
    successfulUpload,
    setSuccessfulUpload,
    secureExitModal,
    returnEditingView,
    cleanImg,
    onLoadImage,
  };

  return <cropImageContext.Provider value={value}>{children}</cropImageContext.Provider>;
};

export const useCropImageContext = () => useContext(cropImageContext);
